import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { PipelineConfig, PipelineConfigService, AlertService } from '@selfai-platform/shared';
import {
  LLMApp,
  LLMAppsCategory,
  LLMAppsResponse,
  CreateThreadRequest,
  CreateThreadResponse,
  RunAppRequest,
  RunAppResponse,
  ThreadHistoryResponse,
  ThreadMessage,
  AppResponse,
  BackendThreadMessage,
  BackendThreadHistoryResponse,
  UserThread,
  GetAllThreadsResponse,
  HealthStatus
} from '../types/service-interfaces';


@Injectable({
  providedIn: 'root',
})
export class LLMOrchestratorService {
  private readonly baseUrl = this.getChatAgentUrl();
  private readonly healthStatusSubject = new BehaviorSubject<HealthStatus | null>(null);
  private readonly availableAppsSubject = new BehaviorSubject<LLMAppsResponse | null>(null);

  public availableApps$ = this.availableAppsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private readonly pipelineConfigService: PipelineConfigService,
    private readonly alertService: AlertService,
  ) {}

  private getHttpHeaders(includeAuth: boolean = true): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json, text/plain, */*',
      'X-Requested-With': 'XMLHttpRequest',
    });

    if (includeAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return headers;
  }

  private getAuthToken(): string | null {
    return (
      localStorage.getItem('authToken') ||
      localStorage.getItem('access_token') ||
      sessionStorage.getItem('authToken') ||
      sessionStorage.getItem('access_token') ||
      null
    );
  }

  private getChatAgentUrl(): string {
    const config = this.pipelineConfigService.getConfig();
    const chatAgentUrl = config?.hosts?.chatAgent;

    if (!chatAgentUrl) {
      const errorMessage = this.translateService.instant('chat.errors.chat-agent-not-enabled');
      this.alertService.error(errorMessage);
      throw new Error(errorMessage);
    }

    return chatAgentUrl;
  }

  private handleHttpError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = this.translateService.instant('chat.errors.unknown-error');

    if (error.error instanceof ErrorEvent) {
      errorMessage = this.translateService.instant('chat.errors.client-error', { message: error.error.message });
    } else {
      switch (error.status) {
        case 0:
          errorMessage = this.translateService.instant('chat.errors.connection-error');
          break;
        case 401:
          errorMessage = this.translateService.instant('chat.errors.unauthorized');
          break;
        case 403:
          errorMessage = this.translateService.instant('chat.errors.forbidden');
          break;
        case 404:
          errorMessage = this.translateService.instant('chat.errors.not-found');
          break;
        case 500:
          errorMessage = this.translateService.instant('chat.errors.server-error');
          break;
        default:
          errorMessage = this.translateService.instant('chat.errors.http-error', {
            status: error.status,
            message: error.message
          });
      }
    }

    this.alertService.error(`${this.translateService.instant('chat.errors.llm-orchestrator-http-error')}: ${errorMessage} - ${error.message || error}`);

    return throwError(() => new Error(errorMessage));
  }

  public checkHealth(): Observable<HealthStatus> {
    const options = {
      headers: this.getHttpHeaders(true),
    };

    return this.http.get<HealthStatus>(`${this.baseUrl}/api/v1/health`, options).pipe(
      tap((status) => this.healthStatusSubject.next(status)),
      catchError((error) => {
        const unhealthyStatus: HealthStatus = {
          status: 'unhealthy',
          timestamp: new Date().toISOString(),
          details: error,
        };
        this.healthStatusSubject.next(unhealthyStatus);
        return this.handleHttpError(error);
      })
    );
  }

  public getApps(): Observable<LLMAppsResponse> {
    const options = {
      headers: this.getHttpHeaders(true),
    };

    return this.http.get<LLMAppsResponse>(`${this.baseUrl}/api/v1/apps`, options).pipe(
      tap((apps) => this.availableAppsSubject.next(apps)),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-get-available-apps') + ': ' + (error.message || error));
        return this.handleHttpError(error);
      })
    );
  }

  public createThread(appId: string): Observable<CreateThreadResponse> {
    const options = {
      headers: this.getHttpHeaders(true),
    };

    const requestBody: CreateThreadRequest = {
      app_id: appId,
    };

    return this.http.post<CreateThreadResponse>(`${this.baseUrl}/api/v1/threads`, requestBody, options).pipe(
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-create-thread-for-app') + ` ${appId}: ` + (error.message || error));
        return this.handleHttpError(error);
      })
    );
  }

  public runApp(request: RunAppRequest): Observable<RunAppResponse> {
    const options = {
      headers: this.getHttpHeaders(true),
    };

    return this.http.post<RunAppResponse>(`${this.baseUrl}/api/v1/run`, request, options).pipe(
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-run-app') + ': ' + (error.message || error));
        return this.handleHttpError(error);
      })
    );
  }

  public getThreadHistory(threadId: string, limit?: number): Observable<ThreadHistoryResponse> {
    const options = {
      headers: this.getHttpHeaders(true),
      params: limit ? { limit: limit.toString() } : {},
    };

    return this.http.get<BackendThreadHistoryResponse>(`${this.baseUrl}/api/v1/threads/history/${threadId}`, options).pipe(
      map((backendResponse) => this.convertBackendThreadHistory(backendResponse)),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-get-thread-history-for-thread') + ` ${threadId}: ` + (error.message || error));
        return this.handleHttpError(error);
      })
    );
  }

  private convertBackendThreadHistory(backendResponse: BackendThreadHistoryResponse): ThreadHistoryResponse {
    const messages: ThreadMessage[] = [];

    backendResponse.messages.forEach((msg) => {
      if (msg.user_message) {
        messages.push({
          role: 'user',
          content: msg.user_message,
          timestamp: msg.timestamp,
          metadata: msg.input_params
        });
      }

      if (msg.app_response?.output?.primary) {
        const primary = msg.app_response.output.primary;
        const content = primary.explanation || primary.response || primary.reply || '';

        messages.push({
          role: 'assistant',
          content: content,
          timestamp: msg.timestamp,
          metadata: {
            ...msg.app_response.metadata,
            debug: msg.app_response.debug,
            errors: msg.app_response.errors,
            warnings: msg.app_response.output?.warnings,
            suggestions: msg.app_response.suggestions,
            codeBlock: primary.code,
            explanation: primary.explanation,
            reply: primary.reply,
            response: primary.response,
            type: primary.type
          }
        });
      }
    });

    return {
      messages,
      thread_id: backendResponse.thread_id,
      metadata: {
        app_id: backendResponse.app_id,
        user_id: backendResponse.user_id,
        created_at: backendResponse.created_at,
        updated_at: backendResponse.updated_at
      }
    };
  }

  public getAllUserThreads(): Observable<GetAllThreadsResponse> {
    const options = {
      headers: this.getHttpHeaders(true),
    };

    return this.http.get<GetAllThreadsResponse>(`${this.baseUrl}/api/v1/threads`, options).pipe(
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-get-all-user-threads') + ': ' + (error.message || error));
        return this.handleHttpError(error);
      })
    );
  }

  public deleteThreadHistory(threadId: string): Observable<void> {
    const options = {
      headers: this.getHttpHeaders(true),
    };

    return this.http.delete<void>(`${this.baseUrl}/api/v1/threads/history/${threadId}`, options).pipe(
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-delete-thread-history-for-thread') + ` ${threadId}: ` + (error.message || error));
        return this.handleHttpError(error);
      })
    );
  }

  public getCurrentUserId(): string {
    return 'default-user';
  }
}
